import { useQuery } from '@tanstack/react-query';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell, PieChart, Pie
} from 'recharts';
import { X, ExternalLink, Check, AlertTriangle, Calendar, Users, Activity } from 'lucide-react';
import { api } from '../lib/api';
import clsx from 'clsx';

const COLORS = ['#22c55e', '#3b82f6', '#64748b'];

export default function ProductDrilldown({ productId, onClose, dateRange, filters }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['product-history', productId, dateRange, filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('days', dateRange?.days || 30);
      if (filters?.campusId) params.set('campusId', filters.campusId);
      if (filters?.userType) params.set('userType', filters.userType);
      if (filters?.visitMetric) params.set('visitMetric', filters.visitMetric);
      return await api.get(`/analytics/product/${productId}/history?${params.toString()}`);
    },
    enabled: !!productId
  });

  if (!productId) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-700 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-surface-200 dark:border-surface-700">
          <div className="flex items-center gap-4">
            {isLoading ? (
              <div className="animate-pulse">
                <div className="h-6 bg-surface-100 dark:bg-surface-700 rounded w-48 mb-2" />
                <div className="h-4 bg-surface-100 dark:bg-surface-700 rounded w-32" />
              </div>
            ) : (
              <>
                <div>
                  <h2 className="text-xl font-bold flex items-center gap-2 text-slate-900 dark:text-white">
                    {data?.product?.name || 'Product Details'}
                    {data?.product?.isApproved ? (
                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-green-500/20 text-green-400 rounded-full">
                        <Check className="w-3 h-3 mr-1" /> Approved
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-amber-500/20 text-amber-400 rounded-full">
                        <AlertTriangle className="w-3 h-3 mr-1" /> Unapproved
                      </span>
                    )}
                  </h2>
                  <p className="text-slate-400 text-sm flex items-center gap-2 flex-wrap">
                    <span className="font-mono">{data?.product?.domain}</span>
                    {data?.product?.category && (
                      <>
                        <span className="text-slate-600">•</span>
                        {data.product.category.split(', ').map((cat, i) => (
                          <span key={i} className="px-1.5 py-0.5 bg-surface-100 dark:bg-surface-700 rounded text-xs">
                            {cat}
                          </span>
                        ))}
                      </>
                    )}
                  </p>
                </div>
              </>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-100 dark:hover:bg-surface-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)] space-y-6">
          {error ? (
            <div className="text-center py-12 text-red-400">
              Failed to load product details
            </div>
          ) : isLoading ? (
            <div className="space-y-6 animate-pulse">
              <div className="grid grid-cols-3 gap-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-24 bg-surface-100 dark:bg-surface-700 rounded-lg" />
                ))}
              </div>
              <div className="h-64 bg-surface-100 dark:bg-surface-700 rounded-lg" />
            </div>
          ) : (
            <>
              {/* Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-surface-100 dark:bg-surface-800 rounded-lg p-4 border border-surface-200 dark:border-surface-700">
                  <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-sm mb-2">
                    <Activity className="w-4 h-4" />
                    Total Visits
                  </div>
                  <div className="text-2xl font-bold text-slate-900 dark:text-white">
                    {data?.stats?.totalVisits?.toLocaleString() || 0}
                  </div>
                </div>
                <div className="bg-surface-100 dark:bg-surface-800 rounded-lg p-4 border border-surface-200 dark:border-surface-700">
                  <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-sm mb-2">
                    <Users className="w-4 h-4" />
                    Unique Devices
                  </div>
                  <div className="text-2xl font-bold text-slate-900 dark:text-white">
                    {data?.stats?.uniqueDevices?.toLocaleString() || 0}
                  </div>
                </div>
                <div className="bg-surface-100 dark:bg-surface-800 rounded-lg p-4 border border-surface-200 dark:border-surface-700">
                  <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-sm mb-2">
                    <Calendar className="w-4 h-4" />
                    First Seen
                  </div>
                  <div className="text-lg font-medium text-slate-900 dark:text-white">
                    {data?.product?.firstSeen
                      ? new Date(data.product.firstSeen).toLocaleDateString()
                      : 'Never'}
                  </div>
                </div>
                <div className="bg-surface-100 dark:bg-surface-800 rounded-lg p-4 border border-surface-200 dark:border-surface-700">
                  <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-sm mb-2">
                    <Calendar className="w-4 h-4" />
                    Last Seen
                  </div>
                  <div className="text-lg font-medium text-slate-900 dark:text-white">
                    {data?.product?.lastSeen
                      ? new Date(data.product.lastSeen).toLocaleDateString()
                      : 'Never'}
                  </div>
                </div>
              </div>

              {/* Usage Trend Chart */}
              <div className="bg-surface-100 dark:bg-surface-800 rounded-lg p-4 border border-surface-200 dark:border-surface-700">
                <h3 className="text-lg font-semibold mb-4 text-slate-900 dark:text-white">Usage Over Time</h3>
                {data?.trend?.length > 0 ? (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={data.trend}>
                        <defs>
                          <linearGradient id="colorVisits" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
                        <XAxis
                          dataKey="date"
                          stroke="#64748b"
                          fontSize={11}
                          tickFormatter={(d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        />
                        <YAxis stroke="#64748b" fontSize={11} />
                        <Tooltip
                          content={({ active, payload }) => {
                            if (!active || !payload?.[0]) return null;
                            return (
                              <div className="bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-lg p-3 shadow-lg">
                                <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">{new Date(payload[0].payload.date).toLocaleDateString()}</p>
                                <p className="font-medium text-slate-900 dark:text-white">{payload[0].value.toLocaleString()} visits</p>
                              </div>
                            );
                          }}
                        />
                        <Area
                          type="monotone"
                          dataKey="visits"
                          stroke="#22c55e"
                          fill="url(#colorVisits)"
                          strokeWidth={2}
                          name="Visits"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-slate-500">
                    No usage data available
                  </div>
                )}
              </div>

              {/* Bottom Row: User Type and Campus Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* User Type Breakdown */}
                <div className="bg-surface-100 dark:bg-surface-800 rounded-lg p-4 border border-surface-200 dark:border-surface-700">
                  <h3 className="text-lg font-semibold mb-4 text-slate-900 dark:text-white">By User Type</h3>
                  {data?.byUserType?.length > 0 ? (
                    <div className="h-48 flex items-center">
                      <ResponsiveContainer width="50%" height="100%">
                        <PieChart>
                          <Pie
                            data={data.byUserType}
                            dataKey="visits"
                            nameKey="userType"
                            cx="50%"
                            cy="50%"
                            innerRadius={30}
                            outerRadius={60}
                          >
                            {data.byUserType.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip
                            content={({ active, payload }) => {
                              if (!active || !payload?.[0]) return null;
                              return (
                                <div className="bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-lg p-3 shadow-lg">
                                  <p className="font-medium text-slate-900 dark:text-white capitalize">{payload[0].name}</p>
                                  <p className="text-sm text-slate-600 dark:text-slate-400">{payload[0].value.toLocaleString()} visits</p>
                                </div>
                              );
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="flex-1 space-y-2">
                        {data.byUserType.map((item, idx) => (
                          <div key={item.userType} className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                              />
                              <span className="capitalize">{item.userType || 'Unknown'}</span>
                            </div>
                            <span className="text-slate-400">{item.visits.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="h-48 flex items-center justify-center text-slate-500">
                      No user type data
                    </div>
                  )}
                </div>

                {/* Campus Breakdown */}
                <div className="bg-surface-100 dark:bg-surface-800 rounded-lg p-4 border border-surface-200 dark:border-surface-700">
                  <h3 className="text-lg font-semibold mb-4 text-slate-900 dark:text-white">By Campus</h3>
                  {data?.byCampus?.length > 0 ? (
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.byCampus.slice(0, 5)} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
                          <XAxis type="number" stroke="#64748b" fontSize={11} />
                          <YAxis
                            dataKey="name"
                            type="category"
                            stroke="#64748b"
                            fontSize={11}
                            width={80}
                            tick={{ fill: '#94a3b8' }}
                          />
                          <Tooltip
                            content={({ active, payload }) => {
                              if (!active || !payload?.[0]) return null;
                              return (
                                <div className="bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-lg p-3 shadow-lg">
                                  <p className="font-medium text-slate-900 dark:text-white">{payload[0].payload.name}</p>
                                  <p className="text-sm text-slate-600 dark:text-slate-400">{payload[0].value.toLocaleString()} visits</p>
                                </div>
                              );
                            }}
                          />
                          <Bar dataKey="visits" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-48 flex items-center justify-center text-slate-500">
                      No campus data
                    </div>
                  )}
                </div>
              </div>

              {/* Vendor Info */}
              {data?.product?.vendor && (
                <div className="flex items-center justify-between bg-surface-100 dark:bg-surface-800 rounded-lg p-4 border border-surface-200 dark:border-surface-700">
                  <div>
                    <span className="text-slate-400 text-sm">Vendor</span>
                    <p className="font-medium">{data.product.vendor}</p>
                  </div>
                  <a
                    href={`https://${data.product.domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-secondary flex items-center gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Visit Site
                  </a>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

